// Interval Training state machine (issue #3721): a pure reducer over one session of ten patterns,
// driven by microphone frames and the page's actions. It is Pitch Match with more than one note
// (pitch_match_session.ts), and keeps its rules: no React, no DOM, no timers, no audio. The page
// holds the state in useReducer, dispatches each mic frame, plays each sound request once and
// dispatches `soundStarted` when it really started, polls the audio clock and dispatches `tick`,
// and renders from the state and the selectors below. Tests drive it the same way with synthetic
// frames.
//
// Every time in this module is absolute ms on the audio clock: frames carry it (CaptureFrame.timeMs),
// `soundStarted` carries the time the sound really started at (later than asked after a slow
// soundfont load), and `tick`, `singNow` and `skip` carry the page's reading of the same clock.
// `clockMs` is the latest of them the machine has seen; nothing here reads a wall clock.
//
// Phases, for each pattern:
//
//   listen ──the sound ends, or Sing it back──▶ sing ──the last note's pause──▶ result ──next──▶ listen (the next pattern)
//     ▲                                                                           │ │
//     └───────────────────────Sing again (the same pattern)───────────────────────┘ └──next, after the last──▶ summary
//
//   listen, sing or result ──micFailed, or tooNoisy in sing──▶ failed ──retry──▶ the phase it struck in
//
// Sounds. The machine asks for one sound at a time (`sound`), and a new request always replaces the
// one before it: the page plays each request id once, stops a sound whose request has gone or been
// replaced, and reports when each started; a report for an id the machine has since replaced is
// ignored. The time `soundStarted` carries is the moment the machine times the sound from, so a page
// on a device with output latency reports when the singer hears it start. The machine stores the
// sound's end on the request then, so nothing rebuilds its notes to ask when it stops. A request
// carries what its kind needs, and soundNotesFor turns it into piano notes:
//   - 'pattern': the pattern's notes one after another, its first note first, with nothing played
//     before it (patternSoundNotes). The listen screen's sound, "Hear it again", and the replay
//     after a retry.
//   - 'home': do, the home note, alone (homeSoundNotes), the house button while singing and on the
//     result.
//   - 'homePattern': do for soloNoteMs, homeGapMs of silence, then the pattern from its first note
//     (homePatternSoundNotes), the house button on the listen screen.
//   - 'note': one note of the pattern alone (patternNoteSoundNotes), a tapped syllable under the staff.
//   - 'steps': the pattern walked a step at a time up to a missed jump's note (stepsSoundNotes), "Hear
//     it as steps" on the result screen: from the pattern's first note through each note to it, every
//     leap filled in with the steps between, or from do when the jump is to the first note.
// From a sound's start the selectors read which note is sounding against the clock, so the house's
// do pulses the house icon and a note of the pattern lights on the staff as it sounds, in any phase.
//
// Listen. The machine asks for the 'pattern' sound, and the hint reads "Listen…". The microphone is
// heard - the meter moves - and never scored. When the clock passes the end of the sound the phase
// becomes sing, and scoring starts
// guideTailMs after that end, the tail Pitch Match's play-then-listen waits out so the piano's release
// is never scored as the singer. "Sing it back" (singNow) ends listen early: the request is cleared,
// which tells the page to stop the sound, and scoring starts guideTailMs later. The house button
// plays do and then the pattern again from the top ('homePattern'), which takes the pattern's
// place as the sound listen waits out, so listen ends when that has finished: a singer who taps the
// house as the hint invites still hears the pattern before singing it, and spends no replay on it.
//
// Sing. The guide is silent. The pattern's notes are sung one at a time, each scored on its own with
// the shared note scorer (note_scorer.ts) against intervalNoteMidi(homeMidi, degree) and the level's
// intervalLevelParams. A note ends one of three ways:
//   - locked: the scorer's hold reaches the level's hold time, and the note is on pitch;
//   - Skip: missed, whatever the singer had managed. A sound still asked for or playing on the note
//     is cleared, so the page stops it, and the next note does not listen before guideTailMs after
//     the tap (or, in the tail after a sound, before that tail ends), so its release is not scored
//     either;
//   - the time limit: noteTimeoutMs of scored time with no lock. The note is close (the yellow
//     result, which Pitch Match does not have) if the singer at some point held it within closeCents
//     for the level's hold time, tracked as the scorer tracks its hold but with the wider band, and
//     missed otherwise. A close note lands where the singer was when that close hold was reached,
//     not wherever they drifted to before the limit.
// Five seconds of silence sets `nudged`, which is text and nothing more, exactly Pitch Match's rule.
// Sound with no note in it for tooNoisyAfterMs puts the session in 'failed' with 'tooNoisy'.
//
// Between notes. The sliding dot reaches the next note's column at the lock and the singer moves
// on with it, so the next note listens as soon as it honestly can: after notePauseMs, a short
// guard sized to re-aiming the known-target estimator rather than a pause for the eye. A frame's
// estimate is relative to the note the estimator was aimed at when the frame was analysed, and
// frames do not say which note that was; the page re-aims it by a message to the analysis worker
// when estimatorTargetMidi changes, and that selector already looks ahead to the next note through
// the guard, so by its end the frames arriving were analysed against the next note. Frames in the
// guard are heard and not scored. The tick or frame that reaches its end records the note and opens
// the next with a fresh score, listening from that moment; a frame that ends it is scored against
// the next note in the same step, so the dot passes from one note to the next without a gap. After
// a pattern's last note the screen holds for patternEndPauseMs instead, so the last note's colour
// is seen before the result replaces it.
//
// Sounds while singing pause the listening. On a note with no outcome, "Hear it again" (the
// pattern), the house button (do) and a tapped syllable (that note) each stop the scoring until the
// sound has finished, as Pitch Match's play-then-listen replay does: the reading, the hold, the close
// hold's run, the silence, the noise, the note's scored time and the nudge start over, while
// `wasClose`, where it was reached and `lastCents` stay, being facts about the note so far. Once the
// sound has reported its start and the clock passes its end, scoring resumes guideTailMs later;
// frames in between are heard, not scored, and the house or a syllable tapped before the sound has
// finished replaces it and the wait starts over. "Hear it again" is counted per note, at most
// maxReplays times, and a tap before the note has begun listening - while it is waiting on a sound,
// or in the tail after one, before its first scored frame - is swallowed, so a double tap does not
// spend both replays (Pitch Match's replay does the same, issue #3518) and a tap meant for "Sing it
// back" that lands as the pattern ends does not spend one; the house and a syllable are not
// counted. None of them moves the singer off the note they were on.
//
// Result. The pattern is green when every note was on pitch, yellow when one was close and none
// missed, red when any was missed. One sentence speaks about one note: the first missed, or when
// none was missed the first close one. When that note was reached by a jump (judged against the note
// before it, or do for the first), "Hear it as steps" walks the pattern a step at a time from its
// first note up to it, every leap filled in, or from do to it when it is the first note.
// The house, a syllable and "Hear it as steps" each play their sound and change nothing else. "Sing again" goes back to the same pattern's listen; the first attempt is
// the one that counts, so its notes are kept (`firstAttempt`) however later attempts go, while the
// result screen always shows the attempt just sung. `next` files the pattern's record and opens the
// next pattern's listen with a new sound request, or the summary after the last. The session's score
// is its green patterns, as Pitch Match's is its locked notes.
//
// Failures. The microphone's own failures (micFailed) strike in any phase but the summary, and
// too-noisy in sing; either clears the sound, so the page stops it, and remembers the phase it struck
// in. Retry resumes it: the result as it was; listen with the pattern played again; sing on the same
// note with a fresh score, the earlier notes' results and the note's replay count kept, and the
// pattern played again first (not counted as a replay), scoring resuming after it as above.

import {
  intervalNoteMidi,
  IntervalPattern,
  isLeap,
  Solfege,
  solfegeFor,
  stepsBetween,
} from '../../../../../common/src/interval_training.ts';
import {
  intervalLevelParams,
  intervalTunables,
  learnListeningTunables,
} from '../../../../../common/src/learn_rules.ts';
import { LearnLevel } from '../../../../../common/src/model.ts';
import { frameDelta } from '../audio/frame_reading.ts';
import { CaptureFrame, MicFailure } from '../audio/learn_mic_capture.ts';
import {
  freshNoteScore,
  hearFrame,
  holdProgressFor,
  noReading,
  NoteScore,
  NoteScoringParams,
  pitchHigherText,
  pitchHoldText,
  pitchLouderText,
  pitchLowerText,
  readingDot,
  ReadingHint,
  readingHint,
  scoreFrame,
} from '../pitch/note_scorer.ts';

// The listening thresholds every Learn exercise shares, plus Interval Training's own timings; both
// live in learn_rules.ts so the device validation pass tunes them in one place
const tunables = { ...learnListeningTunables, ...intervalTunables };

// ---- Sounds ----------------------------------------------------------------------------------

// One piano note of a sound: which degree, when it starts after the sound's own start, and how
// long it sounds. A note of the pattern carries its index in the pattern; the house's do and the
// notes of a walk in steps carry none.
export interface SoundNote {
  degree: number;
  offsetMs: number;
  durationMs: number;
  patternIndex?: number;
}

// What the page is asked to play, with whatever its kind needs (see Sounds in the header)
export type IntervalSound =
  | { kind: 'pattern' }
  | { kind: 'home' }
  | { kind: 'homePattern' }
  | { kind: 'note'; patternIndex: number } // the note's place in the pattern
  | { kind: 'steps'; degrees: number[] };

export type IntervalSoundRequest = IntervalSound & {
  id: number; // the page plays each id once
  atMs?: number; // when it really started, from soundStarted
  endsMs?: number; // when its last note ends, set with atMs
};

// The listen screen's sound: each note of the pattern back to back from the sound's start. The
// machine reads it to light the notes and to know when the sound ends; the page reads it to schedule
// the notes.
export function patternSoundNotes(pattern: IntervalPattern): SoundNote[] {
  return pattern.degrees.map((degree, patternIndex) => ({
    degree,
    offsetMs: patternIndex * tunables.patternNoteMs,
    durationMs: tunables.patternNoteMs,
    patternIndex,
  }));
}

// The house button's sound: do on its own
export function homeSoundNotes(): SoundNote[] {
  return [{ degree: 0, offsetMs: 0, durationMs: tunables.soloNoteMs }];
}

// The house on the listen screen: do on its own, a gap, then the pattern from its first note, each
// note of it carrying its place so it lights as it sounds
export function homePatternSoundNotes(pattern: IntervalPattern): SoundNote[] {
  const patternStartMs = tunables.soloNoteMs + tunables.homeGapMs;
  const notes = patternSoundNotes(pattern).map((note) => ({ ...note, offsetMs: note.offsetMs + patternStartMs }));
  return [...homeSoundNotes(), ...notes];
}

// A tapped syllable's sound: that note of the pattern on its own, carrying its place so it lights
export function patternNoteSoundNotes(pattern: IntervalPattern, patternIndex: number): SoundNote[] {
  return [{ degree: pattern.degrees[patternIndex], offsetMs: 0, durationMs: tunables.soloNoteMs, patternIndex }];
}

// "Hear it as steps": each degree a short note, back to back
export function stepsSoundNotes(degrees: number[]): SoundNote[] {
  return degrees.map((degree, index) => ({
    degree,
    offsetMs: index * tunables.stepNoteMs,
    durationMs: tunables.stepNoteMs,
  }));
}

// From a sound's start to the end of its last note
export function soundLengthMs(notes: SoundNote[]): number {
  return Math.max(0, ...notes.map((note) => note.offsetMs + note.durationMs));
}

// ---- State -----------------------------------------------------------------------------------

export type IntervalPhase = 'listen' | 'sing' | 'result' | 'summary' | 'failed';

// The phases a failure can strike in, and so the ones retry resumes
export type IntervalResumePhase = 'listen' | 'sing' | 'result';

export type IntervalNoteResult = 'onPitch' | 'close' | 'missed';

export type IntervalPatternColor = 'green' | 'yellow' | 'red';

export type IntervalFailure = MicFailure | 'tooNoisy';

// How a note ended, and where the singer was when it did: the last smoothed reading's cents on the
// note (the locking reading's, for a lock), or undefined when the note had no reading at all. The
// result sentence reads it to say "landed flat".
export interface IntervalNoteRecord {
  result: IntervalNoteResult;
  landedCents?: number;
}

// One pattern, sung: what the summary and "Trickiest today" read
export interface IntervalPatternRecord {
  pattern: IntervalPattern;
  notes: IntervalNoteRecord[];
}

// The note being sung: the scorer's fields (note_scorer.ts) and the machine's own
export interface IntervalNoteState extends NoteScore {
  degree: number;
  targetMidi: number;
  // Frames before it are not scored; undefined while a sound is being waited out (in listen, and in
  // sing while a sound asked for on this note has not finished)
  listenFromMs?: number;
  listeningMs: number; // scored time on this note, so a note nobody resolves still ends
  nudged: boolean; // the silence nudge is showing
  // The close band's running hold, kept as the scorer keeps its own: confident frames within
  // closeCents add their time, and a gap longer than matchGapMs drops it
  closeHold?: { heldMs: number; lastInBandMs: number };
  wasClose: boolean; // the close hold reached the level's hold time at some point on this note
  closeCents?: number; // the smoothed reading's cents on the frame that first set wasClose: where a close note lands
  lastCents?: number; // the latest smoothed reading's cents on this note, kept after it fades
  replays: number; // "Hear it again" taps on this note
  outcome?: { kind: IntervalNoteResult; atMs: number; landedCents?: number };
}

export interface IntervalSessionState {
  phase: IntervalPhase;
  level: LearnLevel;
  homeMidi: number;
  patterns: IntervalPattern[];
  finished: IntervalPatternRecord[]; // one per pattern behind the singer; its length is the current pattern's index
  sung: IntervalNoteRecord[]; // the notes of the current attempt that have ended; its length is the current note's index
  firstAttempt?: IntervalNoteRecord[]; // the pattern's first attempt, kept by Sing again: what next files
  note: IntervalNoteState; // the current note: in listen the pattern's first, in result the last one sung
  sound?: IntervalSoundRequest; // the one sound the page should be playing, if any
  clockMs?: number; // the latest time the machine has seen, from a frame, a tick or the page
  failure?: IntervalFailure;
  failedFrom?: IntervalResumePhase; // in failed: the phase retry resumes
  nextRequestId: number;
}

// ---- Actions ---------------------------------------------------------------------------------

export type IntervalSessionAction =
  | { type: 'frame'; frame: CaptureFrame } // one analysed frame from the mic
  | { type: 'tick'; nowMs: number } // the audio clock, while a sound plays and through the pauses
  | { type: 'soundStarted'; requestId: number; atMs: number } // the page started sound `requestId` at `atMs`
  | { type: 'singNow'; nowMs: number } // "Sing it back" on the listen screen
  | { type: 'skip'; nowMs: number } // "Skip" on the sing screen, stamped with the page's clock
  | { type: 'hearAgain' } // "Hear it again" on the sing screen
  | { type: 'playHome' } // the house button
  | { type: 'playNote'; index: number } // a syllable under the staff; `index` is its place in the pattern
  | { type: 'playSteps' } // "Hear it as steps" on the result screen
  | { type: 'singAgain' } // "Sing again" on the result screen
  | { type: 'next' } // "Next pattern" on the result screen
  | { type: 'micFailed'; failure: MicFailure }
  | { type: 'retry' }; // from failed: the mic is open again, resume where it struck

// A session at its beginning: the first pattern's listen, with its sound asked for. The page builds
// one per run, so the request ids start again from 1 with it.
export function initialIntervalSessionState({
  level,
  homeMidi,
  patterns,
}: {
  level: LearnLevel;
  homeMidi: number;
  patterns: IntervalPattern[];
}): IntervalSessionState {
  return {
    phase: 'listen',
    level,
    homeMidi,
    patterns,
    finished: [],
    sung: [],
    note: freshNote(homeMidi, patterns[0].degrees[0]),
    sound: { id: 1, kind: 'pattern' },
    nextRequestId: 2,
  };
}

export function reduceIntervalSession(
  state: IntervalSessionState,
  action: IntervalSessionAction,
): IntervalSessionState {
  switch (action.type) {
    case 'frame':
      return frame(state, action.frame);
    case 'tick':
      return tick(state, action.nowMs);
    case 'soundStarted':
      return soundStarted(state, action.requestId, action.atMs);
    case 'singNow':
      return singNow(state, action.nowMs);
    case 'skip':
      return skip(state, action.nowMs);
    case 'hearAgain':
      return hearAgain(state);
    case 'playHome':
      return playHome(state);
    case 'playNote':
      return playNote(state, action.index);
    case 'playSteps':
      return playSteps(state);
    case 'singAgain':
      return singAgain(state);
    case 'next':
      return next(state);
    case 'micFailed':
      return micFailed(state, action.failure);
    case 'retry':
      return retry(state);
  }
}

// ---- Transitions -----------------------------------------------------------------------------

// A note at its beginning: nothing accumulated, nothing scored yet. The meter level and the last
// frame's time carry over from the note before it (freshNoteScore), so the meter does not blink and
// the first frame of this note accounts for its own elapsed time.
function freshNote(homeMidi: number, degree: number, previous?: NoteScore): IntervalNoteState {
  return {
    ...freshNoteScore(previous),
    degree,
    targetMidi: intervalNoteMidi(homeMidi, degree),
    listenFromMs: undefined,
    listeningMs: 0,
    nudged: false,
    closeHold: undefined,
    wasClose: false,
    closeCents: undefined,
    lastCents: undefined,
    replays: 0,
    outcome: undefined,
  };
}

function withNote(state: IntervalSessionState, note: IntervalNoteState): IntervalSessionState {
  return { ...state, note };
}

function currentPattern(state: IntervalSessionState): IntervalPattern {
  return state.patterns[Math.min(state.finished.length, state.patterns.length - 1)];
}

function levelParams(state: IntervalSessionState): NoteScoringParams {
  return intervalLevelParams[state.level];
}

// The notes of the sound the machine has asked for, by its kind
function soundNotesFor(state: IntervalSessionState, sound: IntervalSoundRequest): SoundNote[] {
  switch (sound.kind) {
    case 'pattern':
      return patternSoundNotes(currentPattern(state));
    case 'home':
      return homeSoundNotes();
    case 'homePattern':
      return homePatternSoundNotes(currentPattern(state));
    case 'note':
      return patternNoteSoundNotes(currentPattern(state), sound.patternIndex);
    case 'steps':
      return stepsSoundNotes(sound.degrees);
  }
}

// Ask the page for a sound: the next request id, its start not yet reported. It replaces whatever
// was asked for before.
function requestSound(state: IntervalSessionState, sound: IntervalSound): IntervalSessionState {
  return { ...state, sound: { ...sound, id: state.nextRequestId }, nextRequestId: state.nextRequestId + 1 };
}

// When the sound asked for stops, once it has reported its start
function soundEndsMs(state: IntervalSessionState): number | undefined {
  return state.sound?.endsMs;
}

// The latest time the machine has seen. The state itself comes back when the reading is not new,
// so a tick that tells the machine nothing - most of them, since the page polls the clock every
// animation frame - is a no-op the reducer's caller can bail out of rather than a re-render.
function withClock(state: IntervalSessionState, nowMs: number): IntervalSessionState {
  if (state.clockMs !== undefined && state.clockMs >= nowMs) {
    return state;
  }
  return { ...state, clockMs: nowMs };
}

function soundStarted(state: IntervalSessionState, requestId: number, atMs: number): IntervalSessionState {
  const sound = state.sound;
  if (!sound || sound.id !== requestId || sound.atMs !== undefined) {
    return state; // a request the machine has since replaced, or one already reported
  }
  return { ...state, sound: { ...sound, atMs, endsMs: atMs + soundLengthMs(soundNotesFor(state, sound)) } };
}

// Into sing, or back to listening in it: the note listens from `listenFromMs`
function startSinging(state: IntervalSessionState, listenFromMs: number): IntervalSessionState {
  return { ...state, phase: 'sing', note: { ...state.note, listenFromMs } };
}

// The machine is waiting out a sound: the pattern on the listen screen, or a sound asked for on a
// note still being sung
function isWaitingForSound(state: IntervalSessionState): boolean {
  if (state.phase === 'listen') {
    return true;
  }
  return state.phase === 'sing' && !state.note.outcome && state.note.listenFromMs === undefined;
}

// A sound being waited out has ended when the clock passes its end; scoring waits out its tail.
// Listen ends here.
function afterSound(state: IntervalSessionState): IntervalSessionState {
  if (!isWaitingForSound(state)) {
    return state;
  }
  const endsMs = soundEndsMs(state);
  if (endsMs === undefined || (state.clockMs ?? 0) < endsMs) {
    return state;
  }
  return startSinging(state, endsMs + tunables.guideTailMs);
}

// What a sound asked for on a note still being sung does to it: scoring stops until the sound has
// finished, and everything the listening had built up starts over. `wasClose`, `closeCents` and
// `lastCents` stay, being facts about the note so far. The noise starts over too, which Pitch Match's
// listenAgain does not do: sounds come far more often here, and noise from before one must not trip
// the too-noisy screen just after it.
function pauseListening(note: IntervalNoteState): IntervalNoteState {
  return {
    ...note,
    ...noReading,
    listenFromMs: undefined,
    listeningMs: 0,
    silentMs: 0,
    noisyMs: 0,
    hold: undefined,
    closeHold: undefined,
    nudged: false,
  };
}

// A sound on a note still being sung, which pauses its listening (see the header); only "Hear it
// again" counts as a replay
function soundWhileSinging(state: IntervalSessionState, sound: IntervalSound, isReplay = false): IntervalSessionState {
  const note = state.note;
  return withNote(requestSound(state, sound), { ...pauseListening(note), replays: note.replays + (isReplay ? 1 : 0) });
}

function isSingingNote(state: IntervalSessionState): boolean {
  return state.phase === 'sing' && !state.note.outcome;
}

// "Sing it back": the sound is cut short (the page stops it when the request is gone) and scoring
// starts once its tail has passed. It works before the sound has reported its start, too.
function singNow(state: IntervalSessionState, nowMs: number): IntervalSessionState {
  if (state.phase !== 'listen') {
    return state;
  }
  return startSinging({ ...withClock(state, nowMs), sound: undefined }, nowMs + tunables.guideTailMs);
}

// "Skip": missed, stamped with the page's clock rather than the last frame's, so a skip before any
// frame arrived still holds the pause for its full length. Close counts for nothing here: the
// singer chose to move on. A sound the note is still waiting on is abandoned with it: the request
// is cleared, so the page stops it.
function skip(state: IntervalSessionState, nowMs: number): IntervalSessionState {
  const note = state.note;
  if (!isSingingNote(state)) {
    return state;
  }
  const sound = isWaitingForSound(state) ? undefined : state.sound;
  const outcome = { kind: 'missed' as const, atMs: nowMs, landedCents: note.lastCents };
  return { ...state, sound, note: { ...note, outcome } };
}

// "Hear it again": the pattern once more, counted against the note's replays; a tap past the
// limit, or while the note is already waiting on a sound, does nothing
function hearAgain(state: IntervalSessionState): IntervalSessionState {
  if (!canHearAgain(state)) {
    return state;
  }
  return soundWhileSinging(state, { kind: 'pattern' }, true);
}

// The house button: do. In listen it takes the pattern's place, and listen ends when do has finished.
function playHome(state: IntervalSessionState): IntervalSessionState {
  if (isSingingNote(state)) {
    return soundWhileSinging(state, { kind: 'home' });
  }
  if (state.phase === 'listen') {
    return requestSound(state, { kind: 'homePattern' }); // do, then the pattern again from the top
  }
  if (state.phase === 'result') {
    return requestSound(state, { kind: 'home' });
  }
  return state;
}

// A syllable under the staff: that note on its own, while a note is being sung and on the result
// (canPlayPatternNote). In listen the pattern is already playing it, and in the pause after an
// outcome the screen is about to move on.
function playNote(state: IntervalSessionState, index: number): IntervalSessionState {
  if (!canPlayPatternNote(state) || !Number.isInteger(index) || index < 0) {
    return state;
  }
  if (index >= currentPattern(state).degrees.length) {
    return state;
  }
  if (isSingingNote(state)) {
    return soundWhileSinging(state, { kind: 'note', patternIndex: index });
  }
  return requestSound(state, { kind: 'note', patternIndex: index });
}

// "Hear it as steps": the missed jump filled in, only when there is one
function playSteps(state: IntervalSessionState): IntervalSessionState {
  const degrees = state.phase === 'result' ? intervalStepsDegrees(state) : undefined;
  if (!degrees) {
    return state;
  }
  return requestSound(state, { kind: 'steps', degrees });
}

// "Sing again": the same pattern from its listen. The first attempt is the one that counts, so it
// is kept the first time and never replaced.
function singAgain(state: IntervalSessionState): IntervalSessionState {
  if (state.phase !== 'result') {
    return state;
  }
  return {
    ...requestSound(state, { kind: 'pattern' }),
    phase: 'listen',
    sung: [],
    firstAttempt: state.firstAttempt ?? state.sung,
    note: freshNote(state.homeMidi, currentPattern(state).degrees[0], state.note),
  };
}

// An outcome holds the screen for notePauseMs when another note follows - the guard while the
// estimator is re-aimed at it - and for patternEndPauseMs after the last, so its colour is seen;
// the tick (or the frame) that reaches the end of it records the note and opens the next, or shows
// the result after the last. The next note listens from the end of the guard, or from the end of
// the tail of a sound the note was still waiting out when it ended if that is later: a Skip that
// cut a sound short, or came in the tail after one, leaves its release to ring out unscored. A
// note that ended in its tail already knows where the tail ends (listenFromMs); one that ended
// while the sound itself was asked for or playing does not, and the tail is counted from the Skip,
// which is stamped when the singer heard the sound stop. (Every other outcome comes from a scored
// frame, already past the tail of any sound.)
function afterPause(state: IntervalSessionState, nowMs: number): IntervalSessionState {
  const outcome = state.note.outcome;
  if (state.phase !== 'sing' || !outcome) {
    return state;
  }
  const degrees = currentPattern(state).degrees;
  const isLast = state.sung.length + 1 >= degrees.length;
  const endsMs = outcome.atMs + (isLast ? tunables.patternEndPauseMs : tunables.notePauseMs);
  if (nowMs < endsMs) {
    return state;
  }
  const sung: IntervalNoteRecord[] = [...state.sung, recordOf(outcome)];
  if (isLast) {
    return { ...state, phase: 'result', sung };
  }
  const tailEndsMs = state.note.listenFromMs ?? outcome.atMs + tunables.guideTailMs;
  const listenFromMs = Math.max(endsMs, tailEndsMs);
  const note = { ...freshNote(state.homeMidi, degrees[sung.length], state.note), listenFromMs };
  return { ...state, sung, note };
}

function recordOf(outcome: NonNullable<IntervalNoteState['outcome']>): IntervalNoteRecord {
  return { result: outcome.kind, landedCents: outcome.landedCents };
}

// A note nobody resolves has to end, and silence alone never ends one (a device whose echo
// canceller leaves sound in the room accumulates none of it): this long spent scoring the note with
// no lock ends it, close if the singer came near enough for long enough and missed otherwise.
function afterNoteTimeout(state: IntervalSessionState, timeMs: number): IntervalSessionState {
  const note = state.note;
  if (state.phase !== 'sing' || note.outcome || note.listeningMs < tunables.noteTimeoutMs) {
    return state;
  }
  const outcome = note.wasClose
    ? { kind: 'close' as const, atMs: timeMs, landedCents: note.closeCents }
    : { kind: 'missed' as const, atMs: timeMs, landedCents: note.lastCents };
  return withNote(state, { ...note, outcome });
}

// "Next pattern": the pattern's record is filed - its first attempt - then the next pattern's
// listen with its own sound request, or the summary after the last
function next(state: IntervalSessionState): IntervalSessionState {
  if (state.phase !== 'result') {
    return state;
  }
  const record = { pattern: currentPattern(state), notes: state.firstAttempt ?? state.sung };
  const finished = [...state.finished, record];
  if (finished.length >= state.patterns.length) {
    return { ...state, phase: 'summary', finished, sung: [], firstAttempt: undefined, sound: undefined };
  }
  const pattern = state.patterns[finished.length];
  return {
    ...requestSound(state, { kind: 'pattern' }),
    phase: 'listen',
    finished,
    sung: [],
    firstAttempt: undefined,
    note: freshNote(state.homeMidi, pattern.degrees[0], state.note),
  };
}

// Into failed: the sound is cleared, so the page stops it, and the phase the failure struck in is
// kept for retry
function failed(
  state: IntervalSessionState,
  failure: IntervalFailure,
  failedFrom: IntervalResumePhase | undefined,
): IntervalSessionState {
  return { ...state, phase: 'failed', failure, failedFrom, sound: undefined };
}

// A second failure before a retry keeps the phase the first one struck in
function micFailed(state: IntervalSessionState, failure: MicFailure): IntervalSessionState {
  if (state.phase === 'summary') {
    return state;
  }
  return failed(state, failure, state.phase === 'failed' ? state.failedFrom : state.phase);
}

function retry(state: IntervalSessionState): IntervalSessionState {
  if (state.phase !== 'failed') {
    return state;
  }
  const cleared: IntervalSessionState = { ...state, failure: undefined, failedFrom: undefined };
  switch (state.failedFrom) {
    case 'result':
      return { ...cleared, phase: 'result' };
    case 'sing':
      return retrySinging(cleared);
    default:
      return {
        ...requestSound(cleared, { kind: 'pattern' }),
        phase: 'listen',
        note: freshNote(state.homeMidi, currentPattern(state).degrees[0], state.note),
      };
  }
}

// Back to singing after a failure: the pattern plays again (not a replay the singer asked for) and
// the note starts over once it has finished, with the earlier notes' results and its replay count
// kept. A note that had already ended when the failure struck, in its pause, keeps its result, and
// the singing resumes on the note after it, or the result after the last.
function retrySinging(state: IntervalSessionState): IntervalSessionState {
  const { note } = state;
  const sung = note.outcome ? [...state.sung, recordOf(note.outcome)] : state.sung;
  const degrees = currentPattern(state).degrees;
  if (sung.length >= degrees.length) {
    return { ...state, phase: 'result', sung };
  }
  const replays = note.outcome ? 0 : note.replays;
  return {
    ...requestSound(state, { kind: 'pattern' }),
    phase: 'sing',
    sung,
    note: { ...freshNote(state.homeMidi, degrees[sung.length], note), replays },
  };
}

// The clock moves in listen and sing, and on the result while a sound plays there (so what is lit
// follows it); a tick that tells the machine nothing returns the state itself
function tick(state: IntervalSessionState, nowMs: number): IntervalSessionState {
  if (state.phase === 'result') {
    return isSoundPlaying(state) ? withClock(state, nowMs) : state;
  }
  if (state.phase !== 'listen' && state.phase !== 'sing') {
    return state;
  }
  return afterPause(afterSound(withClock(state, nowMs)), nowMs);
}

// ---- Frames ----------------------------------------------------------------------------------

function frame(state: IntervalSessionState, frame: CaptureFrame): IntervalSessionState {
  if (state.phase !== 'listen' && state.phase !== 'sing') {
    return state;
  }
  const clocked = afterSound(withClock(state, frame.timeMs));
  if (clocked.phase === 'listen') {
    return withNote(clocked, hearFrame(clocked.note, frame)); // the pattern is playing: the meter moves, nothing is scored
  }
  return afterNoteTimeout(takeFrame(clocked, frame), frame.timeMs);
}

// hearFrame for a frame the machine does not score, scoreFrame for one it does (see the header)
function takeFrame(state: IntervalSessionState, frame: CaptureFrame): IntervalSessionState {
  const note = state.note;
  if (note.outcome) {
    // The pause can end without a tick. When this frame ends it and opens the next note, the new
    // note takes the frame - scored if it is already listening - so a singer who moved on with the
    // lock has a dot on the next note from the frame that ends the guard, with no frame between
    // the old note's dot and the new one's.
    const after = afterPause(state, frame.timeMs);
    if (after.phase === 'sing' && !after.note.outcome) {
      return takeFrame(after, frame);
    }
    return withNote(after, hearFrame(after.note, frame));
  }
  if (note.listenFromMs === undefined || frame.timeMs < note.listenFromMs) {
    return withNote(state, hearFrame(note, frame)); // a sound, or its tail
  }
  const delta = frameDelta(note.lastFrameMs, frame.timeMs);
  const params = levelParams(state);
  const { score, locked, tooNoisy } = scoreFrame(note, frame, note.targetMidi, params);
  const lastCents = score.reading?.cents ?? note.lastCents;
  const closeHold = closeHoldAfter(note, score, frame.timeMs, delta);
  const reachesClose = !note.wasClose && (closeHold?.heldMs ?? 0) >= params.holdSeconds * 1000;
  // Scored: this frame's time counts against the note's own limit as well as the scorer's
  // accumulators. Five seconds of nothing is a line of text and no more.
  const scored: IntervalNoteState = {
    ...score,
    listeningMs: note.listeningMs + delta,
    nudged: note.nudged || score.silentMs >= tunables.silenceReplayMs,
    closeHold,
    wasClose: note.wasClose || reachesClose,
    closeCents: reachesClose ? score.reading?.cents : note.closeCents,
    lastCents,
    outcome: locked ? { kind: 'onPitch', atMs: frame.timeMs, landedCents: lastCents } : score.outcome,
  };
  if (tooNoisy) {
    return failed(withNote(state, scored), 'tooNoisy', 'sing');
  }
  return withNote(state, scored);
}

// The close band's hold after one scored frame, by the scorer's own rule with closeCents for the
// tolerance: a confident reading this frame read, within the band, adds the time since the frame
// before it (a run starts from zero); anything else keeps the run through a gap no longer than
// matchGapMs and drops it beyond that. A reading the scorer kept from an earlier frame through a
// dropout is not this frame's, and a weak one never counts, as neither counts for the real hold.
// The reading's cents are already folded when an octave off counts.
function closeHoldAfter(
  note: IntervalNoteState,
  score: IntervalNoteState,
  timeMs: number,
  delta: number,
): IntervalNoteState['closeHold'] {
  const kept =
    note.closeHold && timeMs - note.closeHold.lastInBandMs <= tunables.matchGapMs ? note.closeHold : undefined;
  const reading = score.reading;
  const inBand =
    reading !== undefined &&
    score.readingAtMs === timeMs &&
    !reading.weak &&
    Math.abs(reading.cents) <= tunables.closeCents;
  if (!inBand) {
    return kept;
  }
  return { heldMs: kept ? kept.heldMs + delta : 0, lastInBandMs: timeMs };
}

// ---- Selectors: the session and the sound -------------------------------------------------------

// 0-based, the pattern the session is on: one record is filed as each pattern is left, so their
// number is the index of the pattern in hand (and, on the summary, the length of the session)
export function intervalPatternIndex(state: IntervalSessionState): number {
  return state.finished.length;
}

export function intervalPatternCount(state: IntervalSessionState): number {
  return state.patterns.length;
}

// The pattern on screen; on the summary, the last one sung
export function currentIntervalPattern(state: IntervalSessionState): IntervalPattern {
  return currentPattern(state);
}

// The sound the page should be playing: it plays each id once, and stops it when this goes away or
// comes back with another id
export function intervalSoundRequest(state: IntervalSessionState): IntervalSoundRequest | undefined {
  return state.sound;
}

// The notes of that sound, for the page to schedule from the time it starts it at
export function intervalSoundNotes(state: IntervalSessionState): SoundNote[] {
  return state.sound ? soundNotesFor(state, state.sound) : [];
}

// The note of the sound in the air at the latest clock reading, if any; `soundNotes` is
// intervalSoundNotes(state), passed in by a caller that already has it
function soundingNote(
  state: IntervalSessionState,
  soundNotes: SoundNote[] = intervalSoundNotes(state),
): SoundNote | undefined {
  const { sound, clockMs } = state;
  if (sound?.atMs === undefined || clockMs === undefined) {
    return undefined;
  }
  const intoMs = clockMs - sound.atMs;
  return soundNotes.find((note) => intoMs >= note.offsetMs && intoMs < note.offsetMs + note.durationMs);
}

function isHomeNote(state: IntervalSessionState, note: SoundNote | undefined): boolean {
  const kind = state.sound?.kind;
  return (kind === 'home' || kind === 'homePattern') && note !== undefined && note.patternIndex === undefined;
}

// do is sounding now as the home note - the house button's - rather than as a note of the pattern or
// a step: the house icon pulses
export function isHomeNoteSounding(state: IntervalSessionState): boolean {
  return isHomeNote(state, soundingNote(state));
}

// The index of the pattern note sounding now, lit on the staff, in any phase: the pattern as it
// plays (on the listen screen or replayed while singing) and a tapped syllable. Undefined while the
// house's do sounds, after the last note, and for "Hear it as steps".
export function litPatternNoteIndex(state: IntervalSessionState): number | undefined {
  return soundingNote(state)?.patternIndex;
}

// What the staff and the house show of the sound, worked out from its notes once: the screen asks
// for it on every microphone frame. The same answers as intervalNoteDisplays, litPatternNoteIndex and
// isHomeNoteSounding.
export function intervalSoundView(state: IntervalSessionState): {
  displays: IntervalNoteDisplay[];
  litIndex?: number;
  homeSounding: boolean;
} {
  const soundNotes = intervalSoundNotes(state);
  const sounding = soundingNote(state, soundNotes);
  return {
    displays: noteDisplays(state, soundNotes),
    litIndex: sounding?.patternIndex,
    homeSounding: isHomeNote(state, sounding),
  };
}

// The sound has reported its start and the clock has not reached its end: what the page's clock
// loop runs for. One asked for and not yet started rings nothing to time.
export function isSoundPlaying(state: IntervalSessionState): boolean {
  const endsMs = soundEndsMs(state);
  return endsMs !== undefined && (state.clockMs ?? 0) < endsMs;
}

// ---- Selectors: the notes -------------------------------------------------------------------------

// 0-based, the note of the pattern being sung: one record is kept as each note ends, so their
// number is the index of the note in hand
export function intervalNoteIndex(state: IntervalSessionState): number {
  return state.sung.length;
}

// Where the page aims the known-target estimator: the note being sung, and through the pause after
// an outcome the note that comes next, so the frames its first scored frames come from have already
// been analysed against it. Undefined when nothing is being sung.
export function estimatorTargetMidi(state: IntervalSessionState): number | undefined {
  if (state.phase !== 'sing') {
    return undefined;
  }
  const { note, homeMidi } = state;
  const degrees = currentPattern(state).degrees;
  const following = degrees[state.sung.length + 1];
  if (note.outcome && following !== undefined) {
    return intervalNoteMidi(homeMidi, following);
  }
  return note.targetMidi;
}

function hasBegunListening(note: IntervalNoteState): boolean {
  return note.listenFromMs !== undefined && note.lastFrameMs !== undefined && note.lastFrameMs >= note.listenFromMs;
}

// The pattern has been played, frames are being scored and the singer has not resolved the note yet
export function isListening(state: IntervalSessionState): boolean {
  return state.phase === 'sing' && !state.note.outcome && hasBegunListening(state.note);
}

// "Hear it again" is there to tap: a note being sung that has begun listening - its first scored
// frame has come, so the tail of the sound before it has passed - with replays left. Until then the
// button is greyed out: it takes the place of "Sing it back" as the pattern ends, and a tap meant for
// "Sing it back" that lands just after must not spend a replay. A note waiting on a sound has not
// begun listening either.
export function canHearAgain(state: IntervalSessionState): boolean {
  return isSingingNote(state) && hasBegunListening(state.note) && intervalReplaysLeft(state) > 0;
}

// A syllable under the staff plays its note now: while a note is being sung (not in the pause after
// its outcome) and on the result. In listen the pattern is already playing every note.
export function canPlayPatternNote(state: IntervalSessionState): boolean {
  return isSingingNote(state) || state.phase === 'result';
}

// How many more times "Hear it again" will play the pattern on the note in hand
export function intervalReplaysLeft(state: IntervalSessionState): number {
  return Math.max(0, tunables.maxReplays - state.note.replays);
}

export type IntervalNoteDisplay = 'upcoming' | 'sounding' | 'sounded' | 'current' | IntervalNoteResult;

// One state per note of the pattern on screen. In listen, against the sound: upcoming, sounding or
// sounded. Otherwise against the singing: the notes already ended show their results, the note in
// hand is current until its outcome (a lock turns it green through the pause), and the rest are
// upcoming.
export function intervalNoteDisplays(state: IntervalSessionState): IntervalNoteDisplay[] {
  return noteDisplays(state, intervalSoundNotes(state));
}

function noteDisplays(state: IntervalSessionState, soundNotes: SoundNote[]): IntervalNoteDisplay[] {
  const degrees = currentPattern(state).degrees;
  if (state.phase === 'listen') {
    return degrees.map((_degree, index) => listenDisplay(state, soundNotes, index));
  }
  return degrees.map((_degree, index) => singDisplay(state, index));
}

function listenDisplay(state: IntervalSessionState, soundNotes: SoundNote[], index: number): IntervalNoteDisplay {
  const { sound, clockMs } = state;
  const note = soundNotes.find((each) => each.patternIndex === index);
  if (!note || sound?.atMs === undefined || clockMs === undefined) {
    return 'upcoming';
  }
  const intoMs = clockMs - sound.atMs;
  if (intoMs >= note.offsetMs + note.durationMs) {
    return 'sounded';
  }
  return intoMs >= note.offsetMs ? 'sounding' : 'upcoming';
}

function singDisplay(state: IntervalSessionState, index: number): IntervalNoteDisplay {
  const ended = state.sung[index];
  if (ended) {
    return ended.result;
  }
  if (index !== state.sung.length) {
    return 'upcoming';
  }
  return state.note.outcome?.kind ?? 'current';
}

// ---- Selectors: the sing screen --------------------------------------------------------------

export type IntervalHint = 'listen' | 'sing' | ReadingHint | 'nudge' | 'locked' | 'close' | 'missed';

export const intervalListenText = 'Listen…';

export function intervalSingText(syllable: Solfege): string {
  return `Sing ${syllable}`;
}

// The nudge names a button that works: "Hear it again" while the note has a replay left, and once
// they are spent the note's own syllable under the staff, which plays it and is not counted
export function intervalNudgeText(syllable: Solfege, canHearAgain: boolean): string {
  if (canHearAgain) {
    return `Sing "${syllable}", or tap Hear it again`;
  }
  return `Sing "${syllable}", or tap ${syllable} to hear it`;
}

export const intervalCloseText = 'Close';
export const intervalMissedText = 'Missed';

export function intervalHint(state: IntervalSessionState): IntervalHint {
  const note = state.note;
  if (note.outcome) {
    return outcomeHint(note.outcome.kind);
  }
  if (isWaitingForSound(state)) {
    return 'listen'; // the pattern on the listen screen, or a sound asked for on this note
  }
  if (note.reading) {
    return readingHint(note.reading);
  }
  return note.nudged ? 'nudge' : 'sing';
}

function outcomeHint(kind: IntervalNoteResult): IntervalHint {
  if (kind === 'onPitch') {
    return 'locked';
  }
  return kind;
}

// The words for a hint; `syllable` is the note being sung (intervalSyllable), and `canHearAgain`
// whether "Hear it again" can be tapped (canHearAgain), which the nudge's wording follows
export function intervalHintText(hint: IntervalHint, syllable: Solfege, canHearAgain: boolean): string {
  switch (hint) {
    case 'listen':
      return intervalListenText;
    case 'sing':
      return intervalSingText(syllable);
    case 'nudge':
      return intervalNudgeText(syllable, canHearAgain);
    case 'higher':
      return pitchHigherText;
    case 'lower':
      return pitchLowerText;
    case 'louder':
      return pitchLouderText;
    case 'hold':
    case 'locked':
      return pitchHoldText; // the hold's wording at a full meter, as Pitch Match's lock
    case 'close':
      return intervalCloseText;
    case 'missed':
      return intervalMissedText;
  }
}

// The syllable of the note being sung
export function intervalSyllable(state: IntervalSessionState): Solfege {
  return solfegeFor(state.note.degree);
}

// The line under the hint for a reading an octave off. Pitch Match says "an octave low is fine";
// this module never names an interval (issue #3721), so it says the same thing without one.
export const intervalOctaveLowText = '(singing it lower is fine)';
export const intervalOctaveHighText = '(singing it higher is fine)';

export function intervalOctaveText(state: IntervalSessionState): string | undefined {
  const octave = state.note.reading?.octave;
  if (!octave) {
    return undefined;
  }
  return octave === 'below' ? intervalOctaveLowText : intervalOctaveHighText;
}

export function intervalHoldProgress(state: IntervalSessionState): { heldMs: number; holdMs: number } {
  return holdProgressFor(state.note, levelParams(state), state.note.outcome?.kind === 'onPitch');
}

// Where to draw the singer on the staff, and in which colour (readingDot); a note that ended close
// or missed shows none
export function intervalDot(state: IntervalSessionState): { cents: number; color: 'yellow' | 'green' } | undefined {
  const { reading, outcome } = state.note;
  if (outcome && outcome.kind !== 'onPitch') {
    return undefined;
  }
  return readingDot(reading, outcome?.kind === 'onPitch');
}

// ---- Selectors: results and the summary -------------------------------------------------------

export function patternColor(results: IntervalNoteResult[]): IntervalPatternColor {
  if (results.includes('missed')) {
    return 'red';
  }
  return results.includes('close') ? 'yellow' : 'green';
}

export function patternRecordColor(record: IntervalPatternRecord): IntervalPatternColor {
  return patternColor(record.notes.map((note) => note.result));
}

// The result screen: each note's result for the attempt just sung, how many were on pitch, its colour
export function intervalResultNotes(state: IntervalSessionState): IntervalNoteResult[] {
  return state.sung.map((note) => note.result);
}

export function intervalOnPitchCount(state: IntervalSessionState): number {
  return intervalResultNotes(state).filter((result) => result === 'onPitch').length;
}

export function intervalResultColor(state: IntervalSessionState): IntervalPatternColor {
  return patternColor(intervalResultNotes(state));
}

// The move into a note of a pattern: from the note before it, or from do for the first note, since
// every pattern is sung from home, though do is not played before it. A jump is more than a step; a
// start is the pattern's first note being do itself; a repeat is a note the same as the one before.
interface IntervalMove {
  kind: 'jump' | 'step' | 'start' | 'repeat';
  from: number;
  to: number;
}

function moveInto(degrees: number[], index: number): IntervalMove {
  const to = degrees[index];
  const from = index === 0 ? 0 : degrees[index - 1];
  if (from === to) {
    return { kind: index === 0 ? 'start' : 'repeat', from, to };
  }
  return { kind: isLeap(from, to) ? 'jump' : 'step', from, to };
}

// The note the result screen speaks about: the first missed note of the attempt on screen or, when
// none was missed, the first close one; the move into it, where it landed, and the walk to it
function noteToTalkAbout(
  state: IntervalSessionState,
): { move: IntervalMove; note: IntervalNoteRecord; walk: number[] } | undefined {
  const missed = state.sung.findIndex((note) => note.result === 'missed');
  const index = missed >= 0 ? missed : state.sung.findIndex((note) => note.result === 'close');
  if (index < 0) {
    return undefined;
  }
  const degrees = currentPattern(state).degrees;
  return { move: moveInto(degrees, index), note: state.sung[index], walk: stepsWalk(degrees, index) };
}

// The pattern from its first note up to and including note `index`, every leap along the way filled
// in with the steps between, so the singer hears how the notes they sang lead into the one that got
// away: do – mi – sol with sol missed is do, re, mi, fa, sol; sol – mi with mi missed is sol, fa, mi;
// do – sol – do with the last do missed goes up to sol and back down. When the note is the pattern's
// first, the walk is from do to it, as the singer starts from home. No degree comes twice in a row,
// so a repeated note is walked once.
function stepsWalk(degrees: number[], index: number): number[] {
  const stops = index === 0 ? [0, degrees[0]] : degrees.slice(0, index + 1);
  const walk: number[] = [stops[0]];
  for (let stop = 1; stop < stops.length; stop += 1) {
    walk.push(...stepsBetween(stops[stop - 1], stops[stop]).slice(1));
  }
  return walk;
}

function syllablesText(degrees: number[]): string {
  return degrees.map(solfegeFor).join(', ');
}

type IntervalDirection = 'flat' | 'sharp' | undefined;

// Which way a note was off, said only when it was outside the level's tolerance: within it the
// singer was not off in any way worth naming, and a note with no reading was off in no known way
function directionOf(state: IntervalSessionState, cents: number | undefined): IntervalDirection {
  if (cents === undefined || Math.abs(cents) <= levelParams(state).toleranceCents) {
    return undefined;
  }
  return cents < 0 ? 'flat' : 'sharp';
}

// The result screen's one sentence about that note (noteToTalkAbout); undefined when every note was
// on pitch. It names syllables and up or down, never an interval, a key or a letter.
export function intervalResultSentence(state: IntervalSessionState): string | undefined {
  const talkAbout = noteToTalkAbout(state);
  if (!talkAbout) {
    return undefined;
  }
  const { move, note, walk } = talkAbout;
  const direction = directionOf(state, note.landedCents);
  return `${moveSubject(move)} ${howItWent(note.result, direction)}. ${whatToDo(move, direction, walk)}`;
}

function moveSubject(move: IntervalMove): string {
  const syllable = solfegeFor(move.to);
  if (move.kind === 'start' || move.kind === 'repeat') {
    return syllable;
  }
  return `The ${move.kind} ${move.to > move.from ? 'up' : 'down'} to ${syllable}`;
}

function howItWent(result: IntervalNoteResult, direction: IntervalDirection): string {
  if (result === 'close') {
    return direction ? `was close, a little ${direction}` : 'was close';
  }
  return direction ? `landed ${direction}` : 'got away';
}

function whatToDo(move: IntervalMove, direction: IntervalDirection, walk: number[]): string {
  switch (move.kind) {
    case 'jump':
      return `Hear it as steps first: ${syllablesText(walk)}.`;
    case 'start':
      return 'Tap the house to hear it again.';
    case 'step':
    case 'repeat':
      if (!direction) {
        return 'Hear it again, then sing it back.';
      }
      return direction === 'flat' ? 'Aim a little higher.' : 'Aim a little lower.';
  }
}

// "Hear it as steps": the walk the sentence lists for the note it is about (stepsWalk); undefined
// when that note was not reached by a jump - judged against the note before it, or do for the first -
// or every note was on pitch
export function intervalStepsDegrees(state: IntervalSessionState): number[] | undefined {
  const talkAbout = noteToTalkAbout(state);
  return talkAbout?.move.kind === 'jump' ? talkAbout.walk : undefined;
}

// The summary: green patterns are the score, the longest run of them the best streak, and one
// square per pattern in its colour
export function intervalSessionScore(records: IntervalPatternRecord[]): number {
  return records.filter((record) => patternRecordColor(record) === 'green').length;
}

export function intervalBestStreak(records: IntervalPatternRecord[]): number {
  let best = 0;
  let run = 0;
  for (const record of records) {
    run = patternRecordColor(record) === 'green' ? run + 1 : 0;
    best = Math.max(best, run);
  }
  return best;
}

export function intervalSummarySquares(records: IntervalPatternRecord[]): IntervalPatternColor[] {
  return records.map(patternRecordColor);
}

// "Trickiest today": over every note of the session that was not on pitch, the move into it that
// came up most, the earliest on a tie; undefined when every note was on pitch
export function trickiestMove(records: IntervalPatternRecord[]): string | undefined {
  const counts = new Map<string, { move: IntervalMove; count: number }>(); // in order of first appearance
  for (const record of records) {
    record.notes.forEach((note, index) => {
      if (note.result === 'onPitch') {
        return;
      }
      const move = moveInto(record.pattern.degrees, index);
      const key = `${move.kind} ${move.from}>${move.to}`;
      const seen = counts.get(key);
      counts.set(key, { move, count: (seen?.count ?? 0) + 1 });
    });
  }
  let trickiest: { move: IntervalMove; count: number } | undefined;
  for (const entry of counts.values()) {
    if (!trickiest || entry.count > trickiest.count) {
      trickiest = entry;
    }
  }
  return trickiest && moveText(trickiest.move);
}

function moveText(move: IntervalMove): string {
  if (move.kind === 'start') {
    return `starting on ${solfegeFor(move.to)}`;
  }
  if (move.kind === 'repeat') {
    return `holding ${solfegeFor(move.to)}`;
  }
  return `the ${move.kind} ${solfegeFor(move.from)} to ${solfegeFor(move.to)}`;
}
